import type { CollectionEntityDefinition } from '@gzl10/nexus-sdk'
import { useIdField, useIconField, useColorField, useNameField, useDescriptionField, useTextField } from '@gzl10/nexus-sdk/fields'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const tagsEntity: CollectionEntityDefinition = {
  type: 'collection',
  realtime: 'sync',
  table: 'tags',
  label: { en: 'Tags', es: 'Etiquetas' },
  labelField: 'name',
  timestamps: true,
  audit: true,
  defaultSort: { field: 'name', order: 'asc' },

  hooks: () => ({
    beforeCreate: async (data) => {
      const record = data as Record<string, unknown>
      if (!record['slug'] && record['name']) {
        record['slug'] = slugify(record['name'] as string)
      }
      return data
    },
    beforeUpdate: async (id, data) => {
      const record = data as Record<string, unknown>
      if (record['name'] && !record['slug']) {
        record['slug'] = slugify(record['name'] as string)
      }
      return data
    }
  }),

  fields: {
    id: useIdField(),
    name: useNameField({
      placeholder: { en: 'Tag name', es: 'Nombre de etiqueta' },
      meta: { searchable: true }
    }),
    slug: useTextField({
      label: 'Slug',
      hint: { en: 'URL-friendly identifier (auto-generated if empty)', es: 'Identificador para URLs (auto-generado si vacío)' },
      placeholder: 'my-tag',
      size: 100,
      nullable: false,
      unique: true,
      index: true,
      meta: { searchable: true, showInDisplay: false }
    }),
    description: useDescriptionField({
      searchable: true,
      meta: { searchable: true, showInDisplay: false }
    }),
    color: useColorField({
      label: { en: 'Color', es: 'Color' },
      hint: { en: 'Hex color code (e.g., #3B82F6)', es: 'Código hex de color (ej: #3B82F6)' },
      placeholder: '#3B82F6',
      nullable: true
    }),
    icon: useIconField({ label: { en: 'Icon', es: 'Icono' }, hint: { en: 'MDI icon name (e.g., mdi:tag)', es: 'Nombre de icono MDI (ej: mdi:tag)' }, meta: { showInDisplay: false } })
  },

  indexes: [
    { columns: ['name'] }
  ],

  casl: {
    subject: 'TaxonomyTag',
    permissions: {
      EDITOR: { actions: ['manage'] },
      USER: { actions: ['create', 'read'] },
      VIEWER: { actions: ['read'] }
    }
  }
}
